/**
 * Trusted, pinned hydration runtime for live-data dashboards (Phase 2).
 *
 * This is the ONLY JavaScript that ever runs inside a micro-app iframe. The
 * iframe is opaque-origin (`sandbox="allow-scripts"`, no `allow-same-origin`),
 * so this script cannot reach the parent's auth/DOM. It also cannot fetch:
 * the injected CSP sets `connect-src 'none'`. Data arrives only via
 * origin-checked `postMessage` from the trusted parent.
 *
 * Responsibilities:
 *  - Listen for `{ type: "micro-app:hydrate", bindings: {...} }` messages.
 *  - Replace each `[data-binding="key"]` element's text with the live value
 *    (scalar bindings), or render a chart for `[data-chart][data-binding]`.
 *  - Leave the publish-time fallback content in place on error/no-data.
 *
 * The parent injects this via {@link injectHydrationRuntime} AFTER nh3
 * sanitization (same seam as brand injection on the backend), so the LLM can
 * never smuggle script through it.
 */

import { FRAPPE_CHARTS_UMD } from "./frappeChartsVendor";

/** Marker so we can find/verify the injected block and avoid double-injection. */
export const HYDRATION_MARKER = "data-micro-app-runtime";

/** Marker on the injected, pinned chart library script. */
export const CHART_LIB_MARKER = "data-micro-app-charts";

/**
 * Minimal base CSS for chart containers, injected into <head>. Frappe injects
 * its own SVG/tooltip styles at runtime; this just ensures the container has a
 * sensible box before the chart mounts and a readable font.
 */
const CHART_BASE_CSS =
  "<style>" +
  // The data-chart element is owned by the chart library: force it transparent
  // and borderless so a stray model-applied border/background doesn't draw a
  // second box inside the surrounding card (the card styling lives on an outer
  // wrapper). No padding so the SVG fills the full container.
  "[data-chart]{display:block;width:100%;min-height:0;background:transparent!important;" +
  "border:0!important;box-shadow:none!important;padding:0!important}" +
  ".chart-container,.frappe-chart{font-family:inherit}" +
  // The chart container must establish a positioning context so frappe's
  // absolutely-positioned tooltip anchors to the chart (not a distant ancestor),
  // which fixes the mis-placed hover tip inside our nested card/iframe layout.
  ".chart-container{position:relative!important}" +
  // Restyle frappe's tooltip (.graph-svg-tip) to be clean + on-brand: a light
  // card with a subtle border/shadow instead of the default dark box.
  ".graph-svg-tip{" +
  "background:var(--brand-surface,#ffffff)!important;" +
  "color:var(--brand-text,#1f2937)!important;" +
  "border:1px solid rgba(0,0,0,0.08)!important;" +
  "border-radius:8px!important;" +
  "box-shadow:0 4px 14px rgba(0,0,0,0.12)!important;" +
  "padding:8px 10px!important;" +
  "font-size:12px!important;text-align:left!important" +
  "}" +
  ".graph-svg-tip .title{" +
  "color:var(--brand-text-muted,#6b7280)!important;" +
  "font-weight:600!important;font-size:11px!important;" +
  "text-transform:uppercase!important;letter-spacing:0.03em!important;" +
  "padding:0 0 2px!important;margin:0!important;display:block!important" +
  "}" +
  ".graph-svg-tip .value,.graph-svg-tip strong{" +
  "color:var(--brand-text,#111827)!important;font-weight:700!important" +
  "}" +
  ".graph-svg-tip ul,.graph-svg-tip ol{margin:0!important;padding:0!important}" +
  ".graph-svg-tip li{padding:0!important}" +
  // Recolor the little pointer triangle to match the light card.
  ".graph-svg-tip .svg-pointer{border-top-color:var(--brand-surface,#ffffff)!important}" +
  "</style>";

/** Reserved for any post-lib CSS tweaks (kept separate for clarity). */
const CHART_CSS = "";

import { RUNTIME_BODY } from "./runtimeBody";

/** Re-exported for tests (see runtimeBody.ts). */
export { RUNTIME_BODY };

/**
 * Restrictive CSP for the generated document: no network, no external scripts.
 * `connect-src 'none'` is the key line — even if a rogue script slipped past
 * nh3, it could not beacon data out. Inline script is allowed via
 * `'unsafe-inline'` because our trusted runtime is inline; the iframe's opaque
 * origin + no `allow-same-origin` is the real containment boundary.
 */
const CSP_META =
  `<meta http-equiv="Content-Security-Policy" ` +
  `content="default-src 'self' data: blob:; ` +
  `img-src data: blob:; ` +
  `style-src 'unsafe-inline'; ` +
  `font-src data:; ` +
  `script-src 'unsafe-inline'; ` +
  `connect-src 'none'">`;

/**
 * Strip the document-structure tags nh3 leaves behind (it sanitizes body
 * *content* and drops `<!DOCTYPE>`, `<html>`, `<head>`, `<body>`). We rebuild a
 * clean skeleton around the fragment so the injected CSP lands in a real
 * `<head>` — otherwise browsers mis-scope a `<meta http-equiv>` that appears in
 * body position, which can leak the policy to the parent document.
 */
function stripDocumentTags(html: string): string {
  return html
    .replace(/<!DOCTYPE[^>]*>/gi, "")
    .replace(/<\/?(?:html|head|body)\b[^>]*>/gi, "")
    .trim();
}

/**
 * Wrap a sanitized dashboard fragment in a well-formed HTML document that
 * carries the trusted hydration runtime and a scoped CSP.
 *
 * The CSP MUST live inside a proper `<head>`; a fragment starting with a bare
 * `<meta http-equiv="Content-Security-Policy">` is parsed in body position and
 * the browser may apply it to the embedding page, breaking the parent app.
 *
 * Idempotent: if the runtime marker is already present the HTML is returned
 * unchanged.
 */
export function injectHydrationRuntime(html: string): string {
  if (html.includes(HYDRATION_MARKER)) return html;

  const fragment = stripDocumentTags(html);

  // Dev-time tripwire: an unescaped "</script>" inside either injected script
  // body would silently truncate the script and break every dashboard. Fail
  // loudly at injection time instead.
  if (RUNTIME_BODY.includes("</script") || FRAPPE_CHARTS_UMD.includes("</script")) {
    throw new Error("hydrationRuntime: injected script body contains '</script' — escape it before embedding");
  }

  const scriptBlock = `<script ${HYDRATION_MARKER}>${RUNTIME_BODY}</script>`;

  // Only ship the (larger) chart library when the dashboard actually uses a
  // chart. The runtime's chart adapter no-ops when window.frappe is absent.
  const needsCharts = /\bdata-chart\s*=/.test(fragment);
  const chartLib = needsCharts ? `<script ${CHART_LIB_MARKER}>${FRAPPE_CHARTS_UMD}</script>${CHART_CSS}` : "";

  return (
    "<!DOCTYPE html><html><head>" +
    CSP_META +
    '<meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
    CHART_BASE_CSS +
    "</head><body>" +
    fragment +
    chartLib +
    scriptBlock +
    "</body></html>"
  );
}

/**
 * Parse the bindings manifest embedded in the dashboard HTML.
 * Mirrors the backend `bindings_manifest` block. Returns the list of
 * binding keys the viewer must hydrate.
 */
export function extractBindingKeys(html: string): string[] {
  const BINDINGS_RE = /<script[^>]*\bdata-micro-app-bindings\b[^>]*>([\s\S]*?)<\/script>/i;
  const match = BINDINGS_RE.exec(html);
  if (!match) return [];
  try {
    const parsed = JSON.parse(match[1]);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((b) => (b && typeof b.binding_key === "string" ? b.binding_key : null))
      .filter((k): k is string => k !== null);
  } catch {
    return [];
  }
}
